import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import MessageModal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import CancelButton from '@wix/da-ds/pkg/modals/dsPresets/MessageModal/buttons/CancelButton';
import ConfirmButton from '@wix/da-ds/pkg/modals/dsPresets/MessageModal/buttons/ConfirmButton';
import DestructiveConfirmButton from '@wix/da-ds/pkg/modals/dsPresets/MessageModal/buttons/DestructiveConfirmButton';
import { VisuallyHidden } from '@wix/da-a11y/pkg/components';
import { getMarkRange, getMarkType, isiOS } from '@tiptap/react';
import { useKeyDown } from '@wix/da-hooks/pkg/useKeyDown';
import { isTouch } from '../../../../utils/browser';
import { addMissingProtocol, validateHref } from '../../../../utils/validateHref';
import EditorAnchor from '@wix/da-ds/pkg/Icons/24x24/EditorAnchor';
import IconButtonWithContextMenu from '@wix/da-ds/pkg/Dropdown/Preset/IconButtonWithContextMenuV2/IconButtonWithContextMenu';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import cn from 'classnames';
import { popperDropdownConfig } from '../../../../../extensions/extension-deviation/constants';
import s from './EditLinkMenuModal.scss';
const Form = /*#__PURE__*/ React.forwardRef(({ initialText, existingHref, onSave, isNew, editor }, formRef)=>{
    const { t } = useTranslation('editor');
    const [textValue, setTextValue] = useState(initialText);
    const [hrefValue, setHrefValue] = useState(existingHref);
    // I think it's safe to assume that `existingHref` has been validated since we validate
    // before allowing the modal to submit. Therefore, anything preexisting has been validated.
    const [isHrefValid, setIsHrefValid] = useState(!!existingHref);
    const [shouldShowHrefErrorMessage, setShouldShowHrefErrorMessage] = useState(false);
    const [anchorsOnPage, setAnchorsOnPage] = useState([]);
    const anchorDev = useFlag('editor_anchor');
    const doesUrlFieldHaveError = hrefValue.length > 0 && !isHrefValid;
    const handleSubmit = useCallback((event)=>{
        if (!doesUrlFieldHaveError && textValue !== '' && hrefValue !== '') {
            onSave({
                text: textValue,
                link: addMissingProtocol(hrefValue),
                initialText
            });
        } else {
            setShouldShowHrefErrorMessage(true);
        }
        event.preventDefault();
        return false;
    }, [
        doesUrlFieldHaveError,
        onSave,
        textValue,
        hrefValue,
        initialText
    ]);
    useEffect(()=>{
        const ids = [];
        const windowAnchors = Array.from(document.querySelectorAll('div[data-editor-viewer] a[id]'));
        editor.state.doc.descendants((node)=>{
            if (node.type.name === 'anchor') {
                ids.push(node.attrs.id);
            }
        });
        if (windowAnchors.length > 0) {
            const windowIds = windowAnchors.map((a)=>a.getAttribute('id')).filter((i)=>i !== null && !/site-header/.test(i));
            ids.push(...windowIds);
        }
        if (ids.length) {
            setAnchorsOnPage([
                ...new Set(ids)
            ]);
        }
    }, [
        editor
    ]);
    const handleSetAnchor = useCallback((e)=>{
        setHrefValue(`#${e.target.textContent}`);
        setIsHrefValid(true);
    }, []);
    return /*#__PURE__*/ React.createElement("form", {
        ref: formRef,
        className: s['root'],
        onSubmit: handleSubmit
    }, /*#__PURE__*/ React.createElement(TextInput, {
        id: "text",
        label: t('ext.linkMenu.modal.form.input.text.label'),
        type: "text",
        value: textValue,
        isRequired: true,
        onChange: (event)=>setTextValue(event.target.value)
    }), /*#__PURE__*/ React.createElement("div", {
        className: cn(s['link'], anchorsOnPage.length > 0 && s['anchors'])
    }, /*#__PURE__*/ React.createElement(TextInput, {
        id: "text",
        label: t('ext.linkMenu.modal.form.input.href.label'),
        placeholder: t('ext.linkMenu.modal.form.input.href.placeholder'),
        type: "text",
        value: hrefValue,
        isRequired: true,
        validationResult: doesUrlFieldHaveError ? 'error' : undefined,
        details: doesUrlFieldHaveError && shouldShowHrefErrorMessage ? t('ext.linkMenu.modal.form.input.href.errorMessage') : undefined,
        onFocus: ()=>setShouldShowHrefErrorMessage(false),
        onBlur: ()=>setShouldShowHrefErrorMessage(true),
        onChange: (event)=>{
            setHrefValue(event.target.value.trim());
            setIsHrefValid(validateHref(addMissingProtocol(event.target.value.trim())));
        }
    }), anchorDev && anchorsOnPage.length > 0 && /*#__PURE__*/ React.createElement(IconButtonWithContextMenu, {
        onClick: (e)=>{
            setShouldShowHrefErrorMessage(false);
            e.preventDefault();
        },
        className: s['anchor'],
        icon: EditorAnchor,
        size: "medium",
        items: anchorsOnPage.map((anchor)=>({
                label: anchor,
                onClick: handleSetAnchor
            })),
        tooltipHoverDelay: 500,
        tooltipText: t('ext.linkMenu.modal.form.anchormenu.tooltip'),
        popperManagerProps: popperDropdownConfig
    })), /*#__PURE__*/ React.createElement(VisuallyHidden, null, /*#__PURE__*/ React.createElement("button", {
        type: "submit",
        tabIndex: -1
    }, t('ext.linkMenu.modal.form.a11y.submitButton.label'))));
});
const EditLinkMenuModal = ({ editor, onCancel, onSave, onRemove })=>{
    var _editor_storage_linkBubbleMenuHandler_bubbleMenuOptions, _editor_storage_linkBubbleMenuHandler, _editor_storage;
    const { t } = useTranslation('editor');
    let existingHref = editor.isActive('link') ? editor.getAttributes('link').href : '';
    // If the attrib is not set we probably on IOS so get it from the handler storage
    if (existingHref === '' && isiOS() && !(editor === null || editor === void 0 ? void 0 : (_editor_storage = editor.storage) === null || _editor_storage === void 0 ? void 0 : (_editor_storage_linkBubbleMenuHandler = _editor_storage.linkBubbleMenuHandler) === null || _editor_storage_linkBubbleMenuHandler === void 0 ? void 0 : (_editor_storage_linkBubbleMenuHandler_bubbleMenuOptions = _editor_storage_linkBubbleMenuHandler.bubbleMenuOptions) === null || _editor_storage_linkBubbleMenuHandler_bubbleMenuOptions === void 0 ? void 0 : _editor_storage_linkBubbleMenuHandler_bubbleMenuOptions.isNew)) {
        var _editor_storage_linkBubbleMenuHandler_linkNodeAttrs, _editor_storage_linkBubbleMenuHandler1;
        var _editor_storage_linkBubbleMenuHandler_linkNodeAttrs_href;
        existingHref = (_editor_storage_linkBubbleMenuHandler_linkNodeAttrs_href = (_editor_storage_linkBubbleMenuHandler1 = editor.storage.linkBubbleMenuHandler) === null || _editor_storage_linkBubbleMenuHandler1 === void 0 ? void 0 : (_editor_storage_linkBubbleMenuHandler_linkNodeAttrs = _editor_storage_linkBubbleMenuHandler1.linkNodeAttrs) === null || _editor_storage_linkBubbleMenuHandler_linkNodeAttrs === void 0 ? void 0 : _editor_storage_linkBubbleMenuHandler_linkNodeAttrs.href) !== null && _editor_storage_linkBubbleMenuHandler_linkNodeAttrs_href !== void 0 ? _editor_storage_linkBubbleMenuHandler_linkNodeAttrs_href : '';
    }
    const linkRange = getMarkRange(editor.state.selection.$from, getMarkType('link', editor.schema));
    const linkText = linkRange ? editor.state.doc.textBetween(linkRange.from, linkRange.to) : '';
    const selectedText = editor.state.doc.textBetween(editor.state.selection.$from.pos, editor.state.selection.$to.pos);
    const initialText = linkText || selectedText;
    const formRef = useRef(null);
    const handleConfirmClick = useCallback(()=>{
        if (formRef.current) {
            var _formRef_current;
            (_formRef_current = formRef.current) === null || _formRef_current === void 0 ? void 0 : _formRef_current.requestSubmit();
        }
    }, []);
    // If the user presses escape, we should cancel
    useKeyDown({
        Escape: onCancel
    });
    const isNewLink = !existingHref;
    return /*#__PURE__*/ React.createElement(MessageModal, {
        useFixedMobileHeight: true,
        isOpen: true,
        onRequestClose: onCancel,
        heading: isNewLink ? t('ext.linkMenu.modal.title.add') : t('ext.linkMenu.modal.title.edit'),
        footerRight: [
            /*#__PURE__*/ React.createElement(CancelButton, {
                key: "cancel",
                onClick: onCancel,
                className: cn(isTouch() && s['small-cancel'])
            }, t('ext.linkMenu.modal.buttons.cancel')),
            isTouch() && !isNewLink && /*#__PURE__*/ React.createElement(DestructiveConfirmButton, {
                onClick: onRemove
            }, t('ext.linkMenu.modal.buttons.remove')),
            /*#__PURE__*/ React.createElement(ConfirmButton, {
                key: "confirm",
                onClick: handleConfirmClick,
                // Hidden because the form has its own submit button
                "aria-hidden": "true"
            }, t('ext.linkMenu.modal.buttons.confirm'))
        ],
        showFooterBorder: true,
        showHeaderBorder: true
    }, /*#__PURE__*/ React.createElement(Form, {
        editor: editor,
        ref: formRef,
        key: `link-at-${linkRange ? `${linkRange.from}-${linkRange.to}` : ''}`,
        initialText: initialText,
        onSave: onSave,
        existingHref: existingHref,
        isNew: isNewLink
    }));
};
EditLinkMenuModal.displayName = 'EditLinkMenuModal';
export default EditLinkMenuModal;

//# sourceMappingURL=EditLinkMenuModal.js.map